import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactLoading from 'react-loading';
import PropTypes from 'prop-types';
import { Typography, Box, Button, Checkbox } from '@mui/material';

import { AGREEMENT_LINK } from './utils';
import Modal from '../../../ui/Modal';
import { acceptTermsAndConditionRequest } from '../../../modules/disputes/thunks';
import { UPDATE_HOUSE } from '../../../modules/house';

const TermAndConditionPopUp = ({
  open,
  closeModal,
  onContinue,
  shouldAcceptTermsAndConditions
}) => {
  const dispatch = useDispatch();

  const house = useSelector(store => store.house) || {};

  const [sendMessageLoading, setMessageLoading] = useState(false);
  const [acceptedTermsAndConditions, setAcceptedTermsAndConditions] = useState(false);
  const [error, setError] = useState('');

  const continueHandler = () => {
    if (!acceptedTermsAndConditions) {
      setError('You must agree to these terms to continue');
    } else {
      setError('');
      setMessageLoading(true);
      dispatch(
        acceptTermsAndConditionRequest(house.houseRef, () => {
          closeModal();
          setMessageLoading(false);
          if (onContinue) onContinue();
          dispatch({
            type: UPDATE_HOUSE,
            data: { ...house, disputeTermsAccepted: true }
          });
        })
      );
    }
  };

  const changeCheckboxHandler = event => {
    const isSelected = event.target.checked;
    setAcceptedTermsAndConditions(isSelected);
    if (isSelected) setError('');
  };

  return (
    <Modal isOpen={open} id="terms-and-condition-modal" centered toggle={closeModal}>
      <Modal.Header toggle={closeModal}>Closing Disputes: Terms and Conditions</Modal.Header>
      <Modal.Body>
        <Typography variant="body2" fontWeight="bold">
          Before you can close a dispute, you must agree you have attempted the following:
        </Typography>
        <Box mt={2}>
          <ul>
            <li>
              <Typography>
                Invoices have been sent in a timely manner including a summary of item(s) won,
                acceptable payment methods, net payment terms and good faith efforts have been made
                to connect with buyer to coordinate payment
              </Typography>
            </li>
            <li>
              <Typography>
                If the buyer pays after a dispute has been closed for a fee credit, you will contact
                Invaluable to reverse the fee credits applied to your account
              </Typography>
            </li>
          </ul>
        </Box>
        <Box mt={3} mb={2}>
          <Typography variant="body2" fontWeight="bold">
            Houses that do not make good faith efforts to reach the bidder before opening and
            closing disputes and/or falsely report unpaid items may:
          </Typography>
          <Box mt={2}>
            <ul>
              <li>
                <Typography>Have all credits for the time period in question reversed</Typography>
              </li>
              <li>
                <Typography>
                  Be subject to suspension under the{' '}
                  <a href={AGREEMENT_LINK} target="_blank" rel="noreferrer">
                    terms of the Master Services Agreement
                  </a>{' '}
                  due to breach of contract
                </Typography>
              </li>
            </ul>
          </Box>
        </Box>
        {shouldAcceptTermsAndConditions && (
          <Box mt={2} id="agreeCheckbox">
            <Typography>
              <Checkbox
                checked={acceptedTermsAndConditions}
                onChange={changeCheckboxHandler}
                size="small"
              />
              I agree to these{' '}
              <a href={AGREEMENT_LINK} target="_blank" rel="noreferrer">
                terms and conditions
              </a>
            </Typography>
          </Box>
        )}
        {error && (
          <Box mt={1}>
            <Typography color="red">{error}</Typography>
          </Box>
        )}
      </Modal.Body>
      <Modal.Footer>
        {shouldAcceptTermsAndConditions ? (
          <Box>
            <Button sx={{ marginLeft: '20px' }} className="cancel-action" onClick={closeModal}>
              Cancel
            </Button>
            <Button className="submit-action" variant="contained" onClick={continueHandler}>
              Continue
              {sendMessageLoading && (
                <ReactLoading className="ml-1" type="spin" height="auto" width={15} />
              )}
            </Button>
          </Box>
        ) : (
          <Button className="submit-action" variant="contained" onClick={closeModal}>
            Cancel
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

TermAndConditionPopUp.propTypes = {
  open: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  shouldAcceptTermsAndConditions: PropTypes.bool,
  onContinue: PropTypes.func
};

TermAndConditionPopUp.defaultProps = {
  onContinue: null,
  shouldAcceptTermsAndConditions: false
};

export default TermAndConditionPopUp;
